import type {
	PartnerGeneralResponseDto,
	PartnerResponseDto,
} from "Infrastructure/Api/Api";
import { PartnerLevel } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useRegisterPartnerPortal } from "Partner/Api/Mutation/registerPartnerPortalMutation";
import { useUpdatePartnerVerticalLevel } from "Partner/Api/Mutation/updatePartnerVerticalLevelMutation";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import {
	AppRegistration,
	ArrowDownward,
	ArrowUpward,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Grid2,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";

type PartnerGeneralInfoWidgetProps = {
	generalInfo: PartnerGeneralResponseDto;
	verticals: PartnerResponseDto["verticals"];
};

const generalInfoResource = Resources.base.partner.detail.generalInfo;

export function PartnerGeneralInfoWidget({
	generalInfo,
	verticals,
}: PartnerGeneralInfoWidgetProps) {
	const { t } = useResource();
	const { mutate } = useUpdatePartnerVerticalLevel(generalInfo.id);
	const { mutate: registerInPortal, isPending: isRegistering } =
		useRegisterPartnerPortal(generalInfo.id);

	return (
		<BaseCard
			title={t(generalInfoResource.title)}
			action={
				<Tooltip title={t(generalInfoResource.registerPartnerPortal)}>
					<span>
						<Button
							variant="outlined"
							size="small"
							startIcon={<AppRegistration />}
							onClick={() => registerInPortal(undefined)}
							disabled={isRegistering}
						>
							{isRegistering
								? t(generalInfoResource.registeringPartnerPortal)
								: t(generalInfoResource.registerPartnerPortal)}
						</Button>
					</span>
				</Tooltip>
			}
		>
			<Grid2 container>
				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.name)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{generalInfo.name}&nbsp;
						{generalInfo.middleName && `${generalInfo.middleName} `}
						{generalInfo.surname}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.email)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{generalInfo.email}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.phone)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{generalInfo.phone}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.verticals)}
					</Typography>
					<Box display="flex" flexDirection="column" gap={0.5}>
						{verticals?.map((v) => (
							<Box
								key={v.id}
								display="flex"
								alignItems="center"
								justifyContent="space-between"
								pr={2}
							>
								<Typography variant="subtitle1">
									{v.name.charAt(0).toUpperCase() + v.name.slice(1)} ({v.level})
								</Typography>
								{v.level === PartnerLevel.Adam ? (
									<Tooltip title="Demote from Adam">
										<IconButton
											size="small"
											color="warning"
											onClick={() =>
												mutate({ verticalId: v.id, level: PartnerLevel.Bronze })
											}
										>
											<ArrowDownward fontSize="small" />
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip title="Promote to Adam">
										<IconButton
											size="small"
											color="primary"
											onClick={() =>
												mutate({ verticalId: v.id, level: PartnerLevel.Adam })
											}
										>
											<ArrowUpward fontSize="small" />
										</IconButton>
									</Tooltip>
								)}
							</Box>
						))}
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.status)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{generalInfo.status}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.teamSize)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{generalInfo.teamSize}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.invoiceIdentifier)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{generalInfo.invoiceIdentifier}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
					<Typography variant="body1" color="secondary" fontWeight={600}>
						{t(generalInfoResource.isCanTrainDay)}
					</Typography>
					<Typography variant="subtitle1" mb={0.5}>
						{generalInfo.isCanTrainDay
							? t(generalInfoResource.trainDaysTrue)
							: t(generalInfoResource.trainDaysFalse)}
					</Typography>
				</Grid2>

				{generalInfo.notes && (
					<Grid2 size={{ xs: 6, md: 4 }} mt={0.5}>
						<Typography variant="body1" color="secondary" fontWeight={600}>
							{t(generalInfoResource.notes)}
						</Typography>
						<Typography variant="subtitle1">{generalInfo.notes}</Typography>
					</Grid2>
				)}
			</Grid2>
		</BaseCard>
	);
}
